/* Settings → Backend: Verbindung, Konto und die Assets auf dem Server (hochladen, laden, herunterladen, löschen). */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type { BackendSession } from '../net/session.ts';
import type { AssetInfo } from '../net/backend.ts';
import { collectDropped } from '../net/dropfiles.ts';
import type { QueueProgress } from '../net/uploadqueue.ts';
import { DialogBody } from './dialog.tsx';
import { useStore, useT } from './hooks.ts';

/** Aktionen, die den Editor betreffen (Dateien laden, Statuszeile) und deshalb von außen kommen */
export interface BackendActions {
  load(path: string): Promise<boolean>;
  download(path: string): Promise<void> | void;
  /** Dateien hochladen; rel = relativer Pfad bei Ordnern (Sounds behalten ihren Ordner unter sound/) */
  upload(files: (File | { file: File; rel: string })[], onProgress?: (p: QueueProgress) => void): Promise<number>;
  loadMapSounds(auto?: boolean): Promise<number>;
  loadMapWads(auto?: boolean): Promise<number>;
  loadMapFgds(auto?: boolean): Promise<number>;
  say(msg: string): void;
  /** Anmelde-Popup öffnen; true bei Erfolg */
  login(): Promise<boolean>;
}

const LOADABLE = /\.(wad|spr|tga|wav|mp3|ogg)$/i;

export function BackendTab(props: { session: BackendSession; actions: BackendActions }) {
  const t = useT();
  const { session, actions } = props;
  const snap = useStore(session);
  const [url, setUrl] = useState(snap.base);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!session.client) void session.connect(); }, [session]);

  const connect = async () => { setBusy(true); try { await session.connect(url); } finally { setBusy(false); } };
  const ok = snap.state === 'ok';

  return (
    <DialogBody>
      <fieldset>
        <legend>{t('be.title')}</legend>
        <div className="row">
          <label htmlFor="xf-be-url">{t('be.url')}</label>
          <input id="xf-be-url" type="text" className="inp" spellCheck={false} value={url} placeholder={window.location.origin || 'http://localhost:8190'}
            onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void connect(); }} />
          <button className="btn" disabled={busy} onClick={() => void connect()}>{t('be.connect')}</button>
        </div>
        <p className="small">{t('be.url_hint')}</p>
        <p className="small" role="status">{snap.state === 'unknown' ? t('be.checking') : ok && snap.info ? t('be.status_ok', { name: snap.info.name, version: snap.info.version }) : t('be.status_down')}</p>
      </fieldset>
      {ok && <Account session={session} actions={actions} />}
      {ok && snap.authed && <Assets session={session} actions={actions} />}
    </DialogBody>
  );
}

function Account(props: { session: BackendSession; actions: BackendActions }) {
  const t = useT();
  const snap = useStore(props.session);
  return (
    <>
      <fieldset>
        <legend>{t('be.account')}</legend>
        {snap.authed ? (
          <div className="row">
            <span>{t('be.signed_in', { email: snap.user?.email || snap.email || '—' }) + (snap.user?.role === 'admin' ? ' (' + t('be.role_admin') + ')' : '')}</span>
            <button className="btn" onClick={() => props.session.logout()}>{t('be.logout')}</button>
          </div>
        ) : (
          <>
            <p className="small">{t('be.need_login')}</p>
            <div className="row"><button className="btn primary" onClick={() => void props.actions.login()}>{t('be.login_open')}</button></div>
          </>
        )}
      </fieldset>
      {!snap.authed && <p className="small">{t('be.login_required')}</p>}
    </>
  );
}

function Assets(props: { session: BackendSession; actions: BackendActions }) {
  const t = useT();
  const { session, actions } = props;
  const snap = useStore(session);
  const [items, setItems] = useState<AssetInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [prog, setProg] = useState<QueueProgress | null>(null);
  const pick = useRef<HTMLInputElement>(null);
  const pickDir = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    const c = session.client;
    if (!c) return;
    try { setItems(await c.list()); setError(null); } catch (err) { if ((err as { status?: number }).status !== 401) setError(t('be.list_failed', { msg: (err as Error).message })); }
  }, [session, t]);
  useEffect(() => { void refresh(); }, [refresh]);

  const upload = async (files: (File | { file: File; rel: string })[]) => {
    if (!files.length) return;
    try { await actions.upload(files, setProg); } finally { setProg(null); }
    await refresh();
  };
  // Ordner im Drop werden durchlaufen; der relative Pfad bleibt erhalten (wichtig für Sounds: sound/ambience/x.wav)
  const onDrop = (e: DragEvent) => { e.preventDefault(); setOver(false); void collectDropped(e.dataTransfer).then((picked) => upload(picked.map((p) => ({ file: p.file, rel: p.rel })))); };
  const canDelete = (it: AssetInfo) => !!snap.user && (snap.user.role === 'admin' || it.owner === snap.user.id);
  const remove = async (it: AssetInfo) => {
    if (!window.confirm(t('be.confirm_delete', { path: it.path }))) return;
    try { await session.client?.remove(it.path); actions.say(t('be.deleted', { path: it.path })); await refresh(); } catch (err) { actions.say(t('be.load_failed', { path: it.path, msg: (err as Error).message })); }
  };

  return (
    <>
      <fieldset>
        <legend>{t('be.upload_title')}</legend>
        <div className={'bedrop' + (over ? ' over' : '')} tabIndex={0} role="button"
          onClick={() => pick.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
          {t('be.drop')}
        </div>
        <input ref={pick} type="file" multiple hidden data-testid="xf-upload" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; void upload(fs); }} />
        <input ref={pickDir} type="file" hidden data-testid="xf-upload-dir" {...{ webkitdirectory: '' }}
          onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; void upload(fs.map((f) => ({ file: f, rel: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name }))); }} />
        <div className="row"><button className="btn sm" onClick={() => pickDir.current?.click()}>{t('be.upload_folder')}</button></div>
        {prog && (
          <div className="prog" data-testid="upload-progress">
            <div className="prog-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(prog.done * 100 / prog.total)} aria-label={t('be.upload_title')}>
              <span style={{ width: (prog.done * 100 / prog.total) + '%' }} />
            </div>
            <span className="small">{prog.waitMs > 0 ? t('be.upload_wait', { done: prog.done, total: prog.total, sec: Math.ceil(prog.waitMs / 1000) }) : t('be.upload_progress', { done: Math.min(prog.done + 1, prog.total), total: prog.total, name: prog.name })}</span>
          </div>
        )}
        <p className="small">{t('be.upload_hint')}</p>
      </fieldset>
      <fieldset>
        <legend>{t('be.assets')}</legend>
        <div className="row"><button className="btn" onClick={() => void actions.loadMapWads()}>{t('be.load_map_wads')}</button></div>
        <div className="belist">
          {error && <p className="small">{error}</p>}
          {items && !items.length && !error && <p className="small">{t('be.none')}</p>}
          {items?.map((it) => (
            <div key={it.path} className="row berow">
              <span className="bepath">{it.path}</span>
              <span className="small">{t('be.size', { kb: Math.max(1, Math.round(it.size / 1024)) })}</span>
              {LOADABLE.test(it.path) && <button className="btn" onClick={async () => { if (await actions.load(it.path)) actions.say(t('be.loaded', { path: it.path })); }}>{t('be.load')}</button>}
              <button className="btn" onClick={() => void actions.download(it.path)}>{t('be.download')}</button>
              {canDelete(it) && <button className="btn" onClick={() => void remove(it)}>{t('be.delete')}</button>}
            </div>
          ))}
        </div>
      </fieldset>
    </>
  );
}
